/**
 * API base for the survey app.
 *
 * The app is normally served from front.servefirst.co.uk (or front-stage…) and
 * calls the API at REACT_APP_BASE_URL, baked at build time. Customers with the
 * CSAT custom-domain add-on load the same build from THEIR hostname (e.g.
 * csat.theircompany.com) via the multi-tenant CloudFront distribution, which
 * routes /api/* on that host to sf-api and strips the prefix. On those hosts we
 * must call same-origin /api so the browser never makes a cross-origin request
 * (the API's CORS allow-list is unchanged and does not know customer hosts).
 *
 * Rule: if the page host is one of OUR known hosts (from REACT_APP_BASE_URL's
 * sibling front host, or localhost for dev), use REACT_APP_BASE_URL as today;
 * otherwise use "/api". Existing hosts are untouched.
 */

/** Hosts that keep using the baked absolute API URL. */
export function isPlatformHost(host) {
  const h = String(host || "").toLowerCase();
  if (!h) return true; // SSR / tests without a window: behave as before
  if (
    h === "localhost" ||
    h.startsWith("localhost:") ||
    h.startsWith("127.0.0.1")
  )
    return true;
  // front.servefirst.co.uk, front-stage.servefirst.co.uk, front-dev…, and the
  // raw CloudFront/S3 hostnames the build is sometimes previewed from.
  return (
    /^front(-[a-z0-9]+)?\.servefirst\.co\.uk$/.test(h) ||
    h.endsWith(".cloudfront.net") ||
    h.endsWith(".amazonaws.com")
  );
}

/**
 * @param {string} [host]  defaults to window.location.host
 * @returns {string} base URL WITHOUT trailing slash: absolute for platform hosts, "/api" for customer hosts
 */
export function getApiBase(host) {
  const h =
    host !== undefined
      ? host
      : typeof window !== "undefined"
        ? window.location.host
        : "";
  if (isPlatformHost(h))
    return String(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  return "/api";
}

export default getApiBase;
